<template>
  <div id="natcon-officers-award-edit" class="professional-award-form">
    <!-- Overlay -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#1976d2"
        ></v-progress-circular>
        <p class="loading-text mt-4">Processing...</p>
      </div>
    </v-overlay>

    <v-card class="main-form-container" elevation="6">
      <!-- Header -->
      <v-card-title class="professional-form-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3">mdi-trophy</v-icon>
              <div class="title-text">
                <h1 class="form-main-title">{{ PageTitle }}</h1>
                <div v-if="row != ''" class="award-badge mt-2">
                  <v-chip color="#ff9800" text-color="white" small>
                    <v-icon left size="14">mdi-star</v-icon>
                    {{ row.AwardTypeTitleTxt }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <router-link to="/national/officers-award/list">
              <v-btn
                color="#ff9800"
                elevation="2"
                rounded
                class="action-btn mr-3 white--text"
              >
                <v-icon left size="18">mdi-arrow-left</v-icon> Back
              </v-btn>
            </router-link>

            <v-btn
              @click.prevent="refreshPageData"
              color="#2ecc71"
              elevation="2"
              rounded
              class="action-btn white--text"
            >
              <v-icon left size="18">mdi-refresh</v-icon> Refresh
            </v-btn>
          </div>
        </div>

        <div class="description-section mt-4" v-if="PageDescription">
          <div class="description-wrapper">
            <v-icon size="20" color="#666" class="mr-2"
              >mdi-information-outline</v-icon
            >
            <p class="form-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="form-main-content pa-6">
        <v-container>
          <!-- Loading -->
          <div class="form-loading-section" v-if="LoadingFlag">
            <div class="loading-wrapper">
              <v-progress-circular
                indeterminate
                color="#1976d2"
                size="48"
                width="4"
              ></v-progress-circular>
              <p class="loading-text mt-4">Loading award details...</p>
            </div>
          </div>

          <!-- Award details -->
          <v-card
            class="award-details-card mb-6"
            elevation="3"
            v-if="row != ''"
          >
            <v-card-title class="award-details-header">
              <v-icon size="24" color="#1976d2" class="mr-3"
                >mdi-certificate</v-icon
              >
              <span class="section-title">Award Details</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Status</div>
                    <div class="detail-value">
                      <v-chip
                        :color="row.AwardStatusColor"
                        text-color="white"
                        class="status-chip"
                        >{{ row.AwardStatusTxt }}</v-chip
                      >
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Register Number</div>
                    <div class="detail-value">
                      {{ row.AwardRegisterNumber }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Title</div>
                    <div class="detail-value">{{ row.AwardTypeTitleTxt }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <!-- NatCon Registration Status Alert -->

          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member is registered for NatCon -->
              <template v-if="NatconRegistrationFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        National Conference Registration Verified
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        The member has successfully
                        <strong
                          >completed the National Conference
                          registration</strong
                        >.
                        <div
                          class="natcon-alert-extra mt-2"
                          style="color: #2e7d32"
                        >
                          You may continue with the award nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Verified
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when not registered (keeps existing look + extra line) -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-account-alert</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        National Conference Registration Required
                      </div>
                      <div class="natcon-alert-msg">
                        The selected member has
                        <strong
                          >not completed the National Conference
                          registration</strong
                        >. Award nomination cannot proceed until registration is
                        done.
                        <div class="natcon-alert-extra mt-2">
                          Please ask the member to finish the NatCon
                          registration process, or contact support if assistance
                          is needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Action Required
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member profile is verified -->
              <template v-if="ProfileStatusFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        Profile Verified ✓
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This member's profile has been verified and is eligible
                        to apply for awards.
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Ready
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when profile is not verified -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-alert-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        ⚠ Profile Not Verified
                      </div>
                      <div class="natcon-alert-msg">
                        The selected member's profile has
                        <strong>not been verified</strong>. Profile verification
                        is required to proceed with award nomination.
                        <div class="natcon-alert-extra mt-2">
                          Please ask the member to complete their profile
                          verification, or contact support if assistance is
                          needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Blocked
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member age is verified -->
              <template v-if="AgeVerificationFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        Eligible by age criteria ✓
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This member's age is eligible to apply for this awards.
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Ready
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when age is not verified -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-alert-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        ⚠ You are not eligible due to Age criteria
                      </div>
                      <div class="natcon-alert-msg">
                        {{ AgeVerificationMsg }}
                        <div class="natcon-alert-extra mt-2">
                          Please contact support if assistance is needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Blocked
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <br />
          <!-- Form -->
          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="getTotalNatConOfficersAwardsDetails"
            class="professional-form"
          >
            <div class="form-content-wrapper">
              <v-card class="criteria-card mb-6" elevation="3" v-if="row != ''">
                <v-card-title class="criteria-header">
                  <v-icon size="24" color="#1976d2" class="mr-3"
                    >mdi-clipboard-text</v-icon
                  >
                  <span class="section-title">Judging Criteria</span>
                </v-card-title>
                <v-card-text class="pa-6">
                  <div v-if="row == ''" class="empty-state text-center pa-4">
                    <h5 class="text-center text-dark">Nil.</h5>
                  </div>

                  <div
                    class="criteria-question-section mb-6"
                    v-for="(item, index) in row.AwardAnswersArray"
                    :key="index"
                  >
                    <div
                      class="criteria-detail-header mb-4"
                      v-if="item.CriteriaTitleFlag"
                    >
                      <div class="criteria-detail-card">
                        <div class="criteria-detail-content">
                          <h3 class="criteria-title">
                            {{ item.CriteriaTitle }}
                            <small>(score - {{ item.CriteriaScore }})</small>
                          </h3>
                        </div>
                      </div>
                    </div>

                    <div class="question-content-wrapper">
                      <div class="question-label-section mb-2">
                        <h6 class="question-title">
                          <span class="required-indicator">*</span>
                          {{ item.QuestionsText }}
                        </h6>
                      </div>

                      <div class="html-editor-wrapper">
                        <vue-editor
                          :id="item.AnswerId"
                          v-model="item.AnswerText"
                          :editor-toolbar="editorOptions.modules.toolbar"
                          :editorOptions="editorOptions"
                          @text-change="onTextChange($event, item)"
                          class="professional-editor"
                        ></vue-editor>
                        <div
                          class="character-counter"
                          :class="{
                            'error--text':
                              getCharacterCount(item.AnswerText) >
                              item.QuestionsCharsCount,
                          }"
                        >
                          {{ getCharacterCount(item.AnswerText) }} /
                          {{ item.QuestionsCharsCount }} characters
                        </div>
                      </div>

                      <!-- Attachments -->
                      <div class="attachments-section mt-4">
                        <div
                          class="attachments-header d-flex justify-space-between align-center"
                        >
                          <h6 class="attachments-title">
                            <v-icon size="20" color="#ff9800" class="mr-2"
                              >mdi-paperclip</v-icon
                            >
                            Attachments
                            <small
                              >(Max: {{ item.QuestionsAttachmentMax }})</small
                            >
                          </h6>
                          <v-btn
                            color="primary"
                            fab
                            small
                            elevation="2"
                            v-if="
                              item.QuestionsAttachmentMax >
                              item.EventDetails.length
                            "
                            @click="addNewRow(item)"
                            ><v-icon>mdi-plus</v-icon></v-btn
                          >
                        </div>

                        <div
                          class="attachments-grid mt-4"
                          v-if="
                            item.EventDetails && item.EventDetails.length > 0
                          "
                        >
                          <v-row>
                            <v-col
                              cols="12"
                              sm="6"
                              md="4"
                              lg="3"
                              v-for="(data, idx) in item.EventDetails"
                              :key="idx"
                              v-if="data.EventId != ''"
                            >
                              <v-card class="attachment-card" elevation="2">
                                <div class="attachment-actions">
                                  <v-btn
                                    icon
                                    color="error"
                                    small
                                    @click="deleteRow(item.SNo, idx)"
                                    ><v-icon>mdi-delete</v-icon></v-btn
                                  >
                                </div>
                                <v-card-text class="pa-4">
                                  <div class="event-images text-center mb-3">
                                    <viewer
                                      rebuild
                                      :images="data.EventImagesArray"
                                    >
                                      <img
                                        class="event-image"
                                        width="100"
                                        height="100"
                                        :src="src"
                                        :key="src"
                                        v-for="src in data.EventImagesArray"
                                      />
                                    </viewer>
                                  </div>
                                  <div class="event-details">
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-calendar</v-icon
                                      ><span class="detail-label"
                                        >Event Date:</span
                                      ><span class="detail-value">{{
                                        data.EventDate
                                      }}</span>
                                    </div>
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-tag</v-icon
                                      ><span class="detail-label"
                                        >Event Name:</span
                                      ><span class="detail-value">{{
                                        data.EventName
                                      }}</span>
                                    </div>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>

                        <div class="no-attachments-state mt-4" v-else>
                          <v-alert
                            type="info"
                            text
                            color="grey lighten-1"
                            outlined
                            dense
                            >No attachments added yet. Click + to add.</v-alert
                          >
                        </div>
                      </div>
                    </div>

                    <v-divider
                      class="mt-4"
                      v-if="index < row.AwardAnswersArray.length - 1"
                    ></v-divider>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Progress -->
              <!-- Other Attachment Details - Modernized -->
              <v-card
                class="attachments-additional-card mb-6"
                elevation="3"
                v-if="row != ''"
              >
                <v-card-title class="attachments-additional-header">
                  <v-icon size="28" color="#ff9800" class="mr-3"
                    >mdi-image-multiple</v-icon
                  >
                  <span class="section-title">Other Attachment Details</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    color="#ff9800"
                    v-if="AllProjectMax > AllProjectCount"
                    @click="addNewRow()"
                    class="add-attachment-action-btn"
                  >
                    <v-icon>mdi-plus-circle</v-icon>
                  </v-btn>
                </v-card-title>

                <v-card-text class="pa-6">
                  <!-- Info Alert -->
                  <v-alert type="info" class="info-alert mb-4">
                    <strong>Image Requirements:</strong> JPG/PNG/JPEG format,
                    maximum 2MB per image, maximum
                    {{ AllProjectMax }} attachments
                  </v-alert>

                  <!-- Existing Attachments -->
                  <div
                    v-if="
                      row.AnswerAttachmentOfficers &&
                      row.AnswerAttachmentOfficers.length > 0
                    "
                    class="mb-6"
                  >
                    <h6 class="attachment-subtitle mb-4">
                      <v-icon size="20" color="#ff9800" class="mr-2"
                        >mdi-file-document</v-icon
                      >
                      Saved Attachments ({{
                        row.AnswerAttachmentOfficers.length
                      }})
                    </h6>
                    <v-row>
                      <v-col
                        v-for="(data, index) in row.AnswerAttachmentOfficers"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        :key="index"
                      >
                        <div class="image-card">
                          <div class="image-gallery-wrapper">
                            <viewer rebuild :images="data.EventImagesArray">
                              <img
                                v-for="(src, idx) in data.EventImagesArray"
                                :key="idx"
                                :src="src"
                                alt="Event Image"
                                class="image-item"
                              />
                            </viewer>
                          </div>
                          <div class="image-delete-action">
                            <v-btn
                              icon
                              color="white"
                              small
                              @click="deleteRow1(index)"
                            >
                              <v-icon size="20">mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <div class="image-details">
                            <div class="detail-item">
                              <v-icon size="16" color="#ff9800" class="mr-1"
                                >mdi-calendar</v-icon
                              >
                              <span class="detail-label">Date:</span>
                              <span class="detail-value">{{
                                data.EventDate
                              }}</span>
                            </div>
                            <div class="detail-item">
                              <v-icon size="16" color="#ff9800" class="mr-1"
                                >mdi-tag</v-icon
                              >
                              <span class="detail-label">Name:</span>
                              <span class="detail-value">{{
                                data.EventName
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- New Attachments -->
                  <div v-if="AllProject && AllProject.length > 0">
                    <h6 class="attachment-subtitle mb-4">
                      <v-icon size="20" color="#ff9800" class="mr-2"
                        >mdi-plus</v-icon
                      >
                      New Attachments ({{ AllProject.length }})
                    </h6>
                    <v-row>
                      <v-col
                        v-for="(data1, k) in AllProject"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        :key="k"
                      >
                        <div class="image-card">
                          <div class="image-gallery-wrapper">
                            <viewer rebuild :images="data1.AnswerEventImageUrl">
                              <img
                                v-for="(src, idx) in data1.AnswerEventImageUrl"
                                :key="idx"
                                :src="src"
                                alt="Event Image"
                                class="image-item"
                              />
                            </viewer>
                          </div>
                          <div class="image-delete-action">
                            <v-btn
                              icon
                              color="white"
                              small
                              @click="deleteRow(k)"
                            >
                              <v-icon size="20">mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <div class="image-details">
                            <div class="detail-item">
                              <v-icon size="16" color="#ff9800" class="mr-1"
                                >mdi-calendar</v-icon
                              >
                              <span class="detail-label">Date:</span>
                              <span class="detail-value">{{
                                data1.AnswerEventDate
                              }}</span>
                            </div>
                            <div class="detail-item">
                              <v-icon size="16" color="#ff9800" class="mr-1"
                                >mdi-tag</v-icon
                              >
                              <span class="detail-label">Name:</span>
                              <span class="detail-value">{{
                                data1.AnswerEventName
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Empty State -->
                  <div
                    v-if="
                      (!row.AnswerAttachmentOfficers ||
                        row.AnswerAttachmentOfficers.length === 0) &&
                      (!AllProject || AllProject.length === 0)
                    "
                    class="empty-state-container"
                  >
                    <v-icon size="48" color="#ce93d8" class="mb-3"
                      >mdi-image-multiple</v-icon
                    >
                    <h6 class="empty-state-text">No attachments yet</h6>
                    <p class="empty-state-description">
                      Click the + button to add new attachments
                    </p>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Video Details - Modernized -->
              <v-card
                class="video-upload-card mb-6"
                elevation="3"
                v-if="row != ''"
              >
                <v-card-title class="video-upload-header">
                  <v-icon size="28" color="white" class="mr-3"
                    >mdi-video</v-icon
                  >
                  <span class="section-title">Video Submission</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <!-- Video Requirements Alert -->
                  <v-alert type="warning" class="video-requirements-alert mb-4">
                    <v-icon color="#d32f2f" class="mr-2"
                      >mdi-alert-circle-outline</v-icon
                    >
                    <strong>Video Requirements:</strong> MP4, AVI, or MOV
                    format, maximum 25MB per video
                  </v-alert>

                  <!-- Existing Video -->
                  <div v-if="row && row.AwardVideoPath" class="mb-6">
                    <h6 class="video-subtitle mb-3">
                      <v-icon size="20" color="#d32f2f" class="mr-2"
                        >mdi-video-check</v-icon
                      >
                      Current Video
                    </h6>
                    <v-card class="video-preview-card" elevation="2">
                      <video
                        :src="row.AwardVideoPath"
                        width="100%"
                        height="300"
                        controls
                        class="video-element"
                      ></video>
                    </v-card>
                  </div>

                  <!-- Upload New Video -->
                  <div>
                    <h6 class="video-subtitle mb-3">
                      <v-icon size="20" color="#d32f2f" class="mr-2"
                        >mdi-video-plus</v-icon
                      >
                      Upload Video
                      <small class="font-weight-light">(optional)</small>
                    </h6>
                    <div class="upload-wrapper">
                      <label class="upload-label">
                        <v-icon size="32" color="#d32f2f" class="mr-2"
                          >mdi-cloud-upload</v-icon
                        >
                        <div class="label-text">
                          <div class="label-main">Choose a video file</div>
                          <div class="label-sub">or drag and drop</div>
                        </div>
                      </label>
                      <input
                        type="file"
                        accept="video/*"
                        class="video-input"
                        @change="onVideoSelected"
                      />
                    </div>
                    <div
                      v-if="UploadVideo && UploadVideo.length > 0"
                      class="video-feedback mt-3"
                    >
                      <v-chip color="#ff9800" text-color="white" small>
                        <v-icon left size="16">mdi-check-circle</v-icon>
                        {{ UploadVideo[0].name }}
                      </v-chip>
                      <v-btn
                        icon
                        small
                        color="#d32f2f"
                        class="ml-2"
                        @click="resetUploadVideo"
                      >
                        <v-icon size="18">mdi-close-circle</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-row wrap v-if="ProgessStart == 1">
                <v-col align="center" cols="12" md="12">
                  <v-progress-circular
                    :rotate="180"
                    :size="100"
                    :width="15"
                    :value="uploadPercentage"
                    color="#00a4ef"
                  >
                    {{ uploadPercentage }}
                  </v-progress-circular>
                  <h3 class="red--text">
                    Please wait, we are uploading your details. Time may vary
                    based on your internet connection.
                  </h3>
                </v-col>
              </v-row>
              <!-- Professional Submit Section -->
              <div class="submit-section" v-if="row != ''">
                <v-card class="submit-card" elevation="3">
                  <v-card-text class="text-center pa-6">
                    <div class="submit-info mb-4">
                      <h3 class="submit-title">Ready to Save Changes?</h3>
                      <p class="submit-description">
                        You can save your progress or submit your final
                        application.
                      </p>
                    </div>

                    <div class="action-buttons">
                      <v-btn
                        @click.prevent="getTotalNatConOfficersAwardsDetails(4)"
                        :loading="SaveFlag"
                        :disabled="
                          !valid1 ||
                          !NatconRegistrationFlag ||
                          !ProfileStatusFlag ||
                          !AgeVerificationFlag
                        "
                        elevation="3"
                        rounded
                        large
                        color="#4caf50"
                        class="submit-btn white--text mr-4"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save Draft
                      </v-btn>

                      <v-btn
                        :disabled="
                          !valid1 ||
                          !NatconRegistrationFlag ||
                          !ProfileStatusFlag ||
                          !AgeVerificationFlag
                        "
                        @click.prevent="getTotalNatConOfficersAwardsDetails(3)"
                        :loading="SubmitFlag"
                        elevation="3"
                        rounded
                        large
                        color="#1976d2"
                        class="submit-btn white--text"
                      >
                        <v-icon left>mdi-send</v-icon>
                        Final Submit
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Attachment Dialog -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="70%">
      <attachment-edit
        pageTitle="Add Project Details"
        :recordData="selectedRecord"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"
      ></attachment-edit>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

import AttachmentEdit from "@/view/pages/erp/jci-award/natcon/common/AttachmentEdit.vue";
import { VueEditor } from "vue2-editor"; // Import Vue-Quill-Editor

export default {
  mixins: [common],
  components: {
    AttachmentEdit,
    VueEditor,
  },
  data() {
    return {
      ProgessStart: 0,
      uploadPercentage: 0,
      NatconRegistrationFlag: false,
      NatconRegistrationCount: "",
      ProfileStatusFlag: false,
      AgeVerificationMsg: "",
      AgeVerificationFlag: false,
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SaveFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      ResultFlag: false,
      CardLoadingFlag: false,
      OverlayLoadingFlag: false,

      UploadFlag: false,
      addButtonFlag: true,

      Status: "",

      NatconOfficersAwardStartDate: "",
      NatconOfficersAwardEndDate: "",
      TotalNatconOfficersAward: 0,
      TotalApplied: 0,
      TotalPartiallySaved: 0,

      StartDateFlag: false,
      EndDateFlag: false,

      editorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Please provide your detailed answer here...",
      },

      UploadVideo: [],
      UploadVideoRules: [
        (value) =>
          !value ||
          value.size < 25000000 ||
          "Upload Video size should be less than 25 MB!",
      ],

      rows: {},

      AwardId: "",
      AwardTypeId: "",

      row: [],
      // CriteriaAnswers: [],
      Project: [],
      AllImages: [],
      AllProject: [],
      AllProjectMax: 10,
      AllProjectCount: 0,

      selectedRecord: "",
      Id: "",
      showTextBox: false,

      addRecordPrompt: false,

      ImageUploadFlag: false,

      AnswerRules: [(v) => !!v || "Answer is required"],
      MissingSteps: "",
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        var zone_name = this.$session.get("ZoneName");
        zone_name = zone_name == (null || undefined) ? 0 : zone_name;

        this.PageTitle = tr.PageTitle + " - " + zone_name;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
    row: function () {
      console.log("watch row called");
      var AwardTypeId = this.row.AwardTypeId;
      console.log({ AwardTypeId });
      this.AwardTypeId = AwardTypeId;
      if (AwardTypeId > 0) {
        this.getAwardTypeDetials();
      }
      this.calculateAllProjectCount();
    },
  },
  mounted() {},
  methods: {
    // HTML Editor related methods
    getCharacterCount(html) {
      if (!html) return 0;
      // Remove HTML tags and entities to count only visible text
      const text = html.replace(
        /<[^>]*>|&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g,
        ""
      );
      return text.length;
    },

    onTextChange(event, item) {
      // Get the Quill editor instance from the event
      const editor = event.editor;
      const maxLength = item.QuestionsCharsCount;

      // Calculate the current text length without HTML tags
      const currentLength = this.getCharacterCount(item.AnswerText);

      // If the length exceeds the limit, prevent further input
      if (currentLength > maxLength) {
        // Get the current selection
        const range = editor.getSelection();

        // If there is no selection, we need to restore the previous text
        if (range && range.length === 0) {
          // Delete the last character
          const position = range.index - 1;
          if (position >= 0) {
            editor.deleteText(position, 1);
          }
        }
      }

      // Update validation rules
      if (currentLength >= maxLength) {
        if (!this.AnswerRules.includes(this.validateMaxLength)) {
          this.AnswerRules.push(this.validateMaxLength);
        }
      } else {
        const index = this.AnswerRules.indexOf(this.validateMaxLength);
        if (index !== -1) {
          this.AnswerRules.splice(index, 1);
        }
      }
    },

    validateMaxLength(v) {
      // This is a custom validation rule for the rich text editor
      // It will be used by Vuetify's validation system
      const item = this.row.AnswerCriteriaOfficers.find(
        (item) => item.AnswerText === v
      );
      if (item && this.getCharacterCount(v) > item.QuestionsCharsCount) {
        return "Character limit exceeded";
      }
      return true;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "national_officers_award",
        Action: "edit",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      var id = this.$route.query.id;
      console.log({ id });
      this.AwardId = id;

      this.getTableRecords();
    },
    resetUploadVideo() {
      console.log("resetUploadVideo called.");
      this.UploadVideo = "";
    },
    onVideoSelected(event) {
      console.log("onVideoSelected called");
      const files = event.target.files;
      if (files && files.length > 0) {
        this.UploadVideo = files;
        console.log("Video selected:", files[0].name);
      }
    },
    resetForm() {
      console.log("resetForm called");
      var rows = this.CriteriaAnswers;

      rows.map((row, index) => {
        row.AnswerText="";
      });

      this.AllProject = {};
      this.AllImages = {};
      this.getTableRecords();
    },
    addNewRow() {
      console.log("addNewRow called");
      this.addRecordPrompt = true;
    },
    deleteRow(id) {
      console.log("deleteRow called");
      console.log({ id });

      this.AllProject.splice(id, 1);
      this.AllImages.splice(id, 1);
      this.calculateAllProjectCount();
    },
    deleteRow1(id) {
      console.log("deleteRow1 called");
      console.log({ id });

      this.row.AnswerAttachmentOfficers.splice(id, 1);

      this.calculateAllProjectCount();
    },
    calculateAllProjectCount() {
      console.log("calculateAllProjectCount called");
      this.AllProjectCount =
        this.row.AnswerAttachmentOfficers.length + this.AllProject.length;
      console.log("AllProjectCount =", this.AllProjectCount);
    },
    hideAddRecordPrompt(tr) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ tr });

      if (tr.EventName != "") {
        this.AllProject.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          AnswerEventDate: tr.EventDate,
          AnswerEventDescription: tr.EventDescription,
          AnswerEventImageName: tr.EventImageName,
          AnswerEventImageUrl: tr.EventImageUrl,
          AnswerEventImage: tr.EventImage,
        });

        this.AllImages.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          UploadedImage: tr.EventImage.UploadedImage,
        });
      }
      console.log("AllProject = " + JSON.stringify(this.AllProject));
    },
    getAwardTypeDetials() {
      console.log("getAwardTypeDetials called");

      var validate1 = true;
      console.log({ validate1 });

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/show";

        var upload = {
          UserInterface: 1,
          AwardType: this.AwardTypeId,
          AwardGroup: 3,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;
        this.NatconRegistrationFlag = false;
        this.NatconRegistrationCount = "";
        this.ProfileStatusFlag = false;
        var ProfileStatusFlag = false;
        this.MissingSteps = "";
        this.AgeVerificationFlag = false;
        this.AgeVerificationMsg = "";
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              // Note: In Edit page, AwardTypeTitleTxt comes from row.AwardTypeTitleTxt
              // So we don't set it here to avoid overwriting
              thisIns.NatconRegistrationFlag = records.NatconRegistrationFlag;
              thisIns.NatconRegistrationCount = records.NatconRegistrationCount;

              var ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.MissingSteps = records.MissingSteps;
              console.log({
                ProfileStatusFlag,
              });

              thisIns.AgeVerificationFlag = records.AgeVerificationFlag;
              thisIns.AgeVerificationMsg = records.AgeVerificationMsg;
              // Check if profile is updated/verified
              if (!ProfileStatusFlag) {
                Swal.fire({
                  icon: "error",
                  title: "Profile Not Verified",
                  html: `<div style="text-align: left;">
          <p><strong>Member Profile Verification Required</strong></p>
          <p>The selected member's profile is not yet verified.</p>
          <p style="margin-bottom: 0; color: #d32f2f;">
            <i class="mdi mdi-alert-circle" style="font-size:20px; vertical-align:middle;"></i>
            Please ensure the member completes their profile verification before applying for this award.
          </p>
        </div>`,
                  confirmButtonColor: "#d32f2f",
                  confirmButtonText: "OK",
                });
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var AwardId = this.AwardId;
      console.log({ AwardId });
      this.ProfileStatusFlag = false;
      var ProfileStatusFlag = false;
      this.MissingSteps = "";
      this.NatconRegistrationFlag = false;
      this.AgeVerificationFlag = false;
      this.AgeVerificationMsg = "";
      if (AwardId > 0) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award/show";
        var upload = {
          UserInterface: 1,
          Award: AwardId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.LoadingFlag = true;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              console.log("records=" + JSON.stringify(records));
              thisIns.row = records;
              var ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.NatconRegistrationFlag = records.NatconRegistrationFlag;
              thisIns.ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.MissingSteps = records.MissingSteps;
              thisIns.AgeVerificationFlag = records.AgeVerificationFlag;
              thisIns.AgeVerificationMsg = records.AgeVerificationMsg;
              // Check if profile is updated/verified
              if (!ProfileStatusFlag) {
                Swal.fire({
                  icon: "error",
                  title: "Profile Not Verified",
                  html: `<div style="text-align: left;">
          <p><strong>Member Profile Verification Required</strong></p>
          <p>The selected member's profile is not yet verified.</p>
          <p style="margin-bottom: 0; color: #d32f2f;">
            <i class="mdi mdi-alert-circle" style="font-size:20px; vertical-align:middle;"></i>
            Please ensure the member completes their profile verification before applying for this award.
          </p>
        </div>`,
                  confirmButtonColor: "#d32f2f",
                  confirmButtonText: "OK",
                });
              }

              // thisIns.toast("success", output);
            } else {
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Award details not found.";
        console.log("error=" + message);
      }
    },
    getTotalNatConOfficersAwardsDetails(status) {
      console.log("getTotalNatConOfficersAwardsDetails called");

      var validate1 = true;
      console.log({ validate1 });

      console.log({ status });
      this.Status = status;

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/total-awards";

        var AwardCategory = 6;
        var AwardGroup = 3;

        if (this.RoleId == 2) {
          // For Admin
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
            Lom: this.LomCode,
          };
        } else {
          // For Others
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
          };
        }

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.rows = records;
              thisIns.NatconOfficersAwardStartDate = records.StartDateTxt;
              thisIns.NatconOfficersAwardEndDate = records.EndDateTxt;
              thisIns.TotalNatconOfficersAward = records.TotalAward;
              thisIns.TotalApplied = records.TotalApplied;
              thisIns.TotalPartiallySaved = records.TotalPartiallySaved;
              thisIns.StartDateFlag = records.StartDateFlag;
              thisIns.EndDateFlag = records.EndDateFlag;

              console.log(
                "StartDateFlag=" +
                  records.StartDateFlag +
                  ", EndDateFlag=" +
                  records.EndDateFlag
              );

              if (thisIns.StartDateFlag && thisIns.EndDateFlag) {
                thisIns.confirmAlert(status);
              } else {
                thisIns.deadlineAlert();
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    deadlineAlert() {
      console.log("deadlineAlert called");
      var htmlTxt = "";
      htmlTxt +=
        "<h3> Natcon Officers Awards Bidding ended at <b> " +
        this.NatconOfficersAwardEndDate +
        " </b> </h3>";
      // htmlTxt += "<br/> Start <b> " + this.NatconOfficersAwardEndDate + " </b>";

      Swal.fire({
        title: "Sorry !",
        text: "",
        icon: "warning",
        html: htmlTxt,
      });
    },
    confirmAlert(status) {
      console.log("confirmAlert called");
      var video_validate = false;
      var validate1 = false;

      // Video validation - check file size manually since it's a native input
      if (this.UploadVideo && this.UploadVideo.length > 0) {
        var videoFile = this.UploadVideo[0];
        video_validate = videoFile.size < 25000000; // 25MB in bytes
      } else {
        video_validate = true; // Video is optional
      }

      validate1 = status == 4 ? true : this.$refs.form1.validate();
      console.log({ status }, { video_validate }, { validate1 });

      if (video_validate && validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.submitForm(status);
          } else {
            this.sweetAlert("error", "You cancelled the process", false);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "kindly answer all the questions.";
        }
        if (!video_validate) {
          message +=
            "The video should be <br/> 1. In MP4 or AVI or MOV file format <br/> 2. Must be less than 25MB in file size";
        }
        Swal.fire({
          // title: "Are you sure?",
          icon: "error",
          html: message,
        });
        // this.sweetAlert("error", message, false);
      }
    },
    checkNatconRegistrationForMember() {
      console.log("checkNatconRegistrationForMember called");

      var validate1 = true;
      console.log({ validate1 });

      if (validate1) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award/natcon-registration-check";

        var upload = {
          UserInterface: 1,
          AwardGroup: 3,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            console.log(
              "natcon-registration-check response=" + JSON.stringify(response)
            );

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              console.log("records=" + JSON.stringify(records));
              thisIns.NatconRegistrationFlag =
                records.NatconRegistrationFlag || false;
            } else {
              thisIns.NatconRegistrationFlag = false;
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.NatconRegistrationFlag = false;
          });
      }
    },
    submitForm(status) {
      console.log("submitForm is called");
      this.ProgessStart = 1;
      this.OverlayLoadingFlag = true;

      if (status == 4) {
        this.SaveFlag = true;
      } else {
        this.SubmitFlag = true;
      }

      console.log({ status });
      var count = this.AllImages.length;

      var video = this.UploadVideo != {} ? this.UploadVideo : {};
      console.log({ video });

      this.AllImages.forEach((row, index) => {
        console.log({ index });
        row.AnswerEventId = index;
        console.log("AllImages=" + JSON.stringify(this.AllImages));
      });

      this.AllProject.forEach((item, index) => {
        console.log({ index });
        item.AnswerEventId = index;
        console.log("AllProject=" + JSON.stringify(this.AllProject));
      });

      var answerData = {
        ActiveStatus: status,
        Award: this.AwardId,
        AwardType: this.AwardTypeId,
        CriteriaAnswers: this.row,
        AnswerAttachmentOfficers: this.row.AnswerAttachmentOfficers,
        AllProject: this.AllProject,
        AllImagesCount: count,
      };
      console.log("answerData=" + JSON.stringify(answerData));

      let upload = new FormData();
      upload.append("UserInterface", 1);
      upload.append("Form", JSON.stringify(answerData));
      upload.append("UploadVideo", video);

      this.AllImages.forEach((row, index) => {
        var id = index + 1;
        console.log({ id });
        var UploadedImage = row["UploadedImage"];
        var EventId = row["AnswerEventId"];
        console.log({ UploadedImage }, { EventId });

        upload.append("AllImages" + index, UploadedImage, EventId);
      });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var add_url = server_url + "api/award/officers-award-update";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);
          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.$router.push("/national/officers-award/list");
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
#natcon-officers-award-edit {
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: "Roboto", Arial, sans-serif;

  .professional-overlay {
    .loading-container {
      text-align: center;
      color: white;
      .loading-text {
        font-size: 1.05rem;
        margin: 0;
        font-weight: 500;
      }
    }
  }

  .main-form-container {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08) !important;
  }

  .professional-form-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e6eef9;
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
    }
    .form-main-title {
      font-size: 1.6rem;
      font-weight: 700;
      color: #263238;
      margin: 0;
    }
    .award-badge {
      margin-top: 0.5rem;
    }
    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }
  }

  .description-section {
    .description-wrapper {
      display: flex;
      align-items: center;
      background: rgba(25, 118, 210, 0.04);
      padding: 0.9rem;
      border-radius: 8px;
      border-left: 4px solid #1976d2;
      .form-description {
        margin: 0;
        color: #455a64;
      }
    }
  }

  .form-main-content {
    padding: 1.5rem 2rem;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  }

  .award-details-card,
  .member-selection-card,
  .criteria-card,
  .submit-card {
    border-radius: 12px !important;
    border: 1px solid #e9f2ff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
  }

  .award-details-header,
  .member-selection-header,
  .criteria-header {
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e6eef9;
    padding: 1rem 1.5rem;
    .section-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #263238;
    }
  }

  .detail-item {
    margin-bottom: 0.5rem;
    .detail-label {
      font-size: 0.9rem;
      color: #1976d2;
      margin-bottom: 6px;
      font-weight: 600;
    }
    .detail-value {
      background: #fbfdff;
      padding: 10px;
      border-radius: 8px;
      color: #263238;
      font-weight: 500;
    }
  }

  .member-selection-card .member-selection-item,
  .member-selection-card .member-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .member-avatar {
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #eef3fb;
  }

  .html-editor-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
  }
  .professional-editor .ql-container {
    min-height: 140px;
  }
  .character-counter {
    padding: 6px 10px;
    font-size: 0.85rem;
    color: #607d8b;
    text-align: right;
    background: #fafafa;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  .character-counter.error--text {
    color: #d32f2f;
    background: rgba(244, 67, 54, 0.06);
    font-weight: 600;
  }

  .criteria-detail-card {
    background: #fff8e1;
    border: 1px solid #ffecb3;
    padding: 12px;
    border-radius: 8px;
  }
  .criteria-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: #bf360c;
    margin: 0;
  }

  .attachments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .attachments-grid .attachment-card {
    border-radius: 8px !important;
    transition: transform 0.18s, box-shadow 0.18s;
  }
  .attachment-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  }

  .event-image {
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    object-fit: cover;
    transition: transform 0.2s;
  }
  .event-image:hover {
    transform: scale(1.04);
  }

  .no-attachments-state {
    text-align: center;
    padding: 1rem;
    color: #9e9e9e;
  }

  .submit-section .submit-card {
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    border: 1px solid #e6f2ff;
  }
  .submit-btn {
    text-transform: none;
    font-weight: 600;
  }

  // Attachments Card Styling
  .attachments-additional-card {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-top: 1.5rem;
    overflow: hidden;

    .attachments-additional-header {
      background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
      border-bottom: 2px solid #ffb74d;
      display: flex;
      align-items: center;
      padding: 1.5rem;
      color: #bf360c;

      .section-title {
        font-size: 1.15rem;
        font-weight: 600;
        color: #bf360c;
        margin: 0;
      }
    }

    .add-attachment-action-btn {
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.15);
      }
    }
  }

  // Info Alert
  .info-alert {
    background-color: rgba(255, 152, 0, 0.04);
    border: 1px solid rgba(255, 152, 0, 0.2);
    border-radius: 8px;
    padding: 1rem;
    color: #bf360c;

    strong {
      color: #ff9800;
    }
  }

  // Attachment Subtitle
  .attachment-subtitle {
    font-size: 0.95rem;
    font-weight: 600;
    color: #bf360c;
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  // Image Card
  .image-card {
    position: relative;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: white;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(255, 152, 0, 0.15);
    }

    .image-gallery-wrapper {
      width: 100%;
      height: 200px;
      background: #fafafa;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      position: relative;

      .image-item {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .image-delete-action {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background: rgba(211, 47, 47, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover .image-delete-action {
      opacity: 1;
    }

    .image-details {
      padding: 1rem;
      background: #fafafa;

      .detail-item {
        display: flex;
        align-items: center;
        margin-bottom: 0.6rem;
        font-size: 0.85rem;

        &:last-child {
          margin-bottom: 0;
        }

        .detail-label {
          font-weight: 600;
          color: #424242;
          min-width: 40px;
        }

        .detail-value {
          color: #666;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  // Empty State
  .empty-state-container {
    padding: 3rem 2rem;
    text-align: center;
    border: 2px dashed #ce93d8;
    border-radius: 8px;
    background: #fafafa;
    margin: 2rem 0;

    .empty-state-text {
      color: #bf360c;
      font-weight: 600;
      font-size: 1rem;
      margin-bottom: 0.5rem;
    }

    .empty-state-description {
      color: #999;
      font-size: 0.9rem;
      margin: 0;
    }
  }

  // Video Upload Card
  .video-upload-card {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-top: 1.5rem;
    overflow: hidden;

    .video-upload-header {
      background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
      border-bottom: 2px solid #ef5350;
      display: flex;
      align-items: center;
      padding: 1.5rem;
      color: white;

      .section-title {
        font-size: 1.15rem;
        font-weight: 600;
        color: #d32f2f;
        margin: 0;
      }
    }
  }

  // Video Requirements Alert
  .video-requirements-alert {
    background-color: rgba(211, 47, 47, 0.04);
    border: 1px solid rgba(211, 47, 47, 0.2);
    border-radius: 8px;
    padding: 1rem;
    color: #d32f2f;

    strong {
      color: #d32f2f;
    }
  }

  // Video Subtitle
  .video-subtitle {
    font-size: 0.95rem;
    font-weight: 600;
    color: #d32f2f;
    display: flex;
    align-items: center;
    margin-bottom: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  // Video Preview Card
  .video-preview-card {
    border-radius: 8px;
    overflow: hidden;
    background: black;
    border: 1px solid #e0e0e0;

    .video-element {
      display: block;
      width: 100%;
    }
  }

  // Upload Wrapper
  .upload-wrapper {
    position: relative;
    border: 2px dashed #ef5350;
    border-radius: 8px;
    padding: 2rem;
    background: #f5f5f5;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #d32f2f;
      background: #fff3e0;
    }

    .upload-label {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      width: 100%;

      .label-text {
        text-align: left;
        margin-left: 1rem;

        .label-main {
          font-weight: 600;
          color: #d32f2f;
          font-size: 0.95rem;
        }

        .label-sub {
          font-size: 0.85rem;
          color: #999;
          margin-top: 0.25rem;
        }
      }
    }

    .video-input {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0;
      cursor: pointer;
      width: 100%;
      height: 100%;
    }
  }

  // Video Feedback
  .video-feedback {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  // Responsive Design
  @media (max-width: 960px) {
    .attachments-additional-card .attachments-additional-header,
    .video-upload-card .video-upload-header {
      padding: 1.2rem;

      .section-title {
        font-size: 1.05rem;
      }
    }

    .image-card .image-gallery-wrapper {
      height: 160px;
    }
  }

  @media (max-width: 600px) {
    .attachments-additional-card,
    .video-upload-card {
      margin-left: 0;
      margin-right: 0;
      border-radius: 8px;
    }

    .attachments-additional-card .attachments-additional-header,
    .video-upload-card .video-upload-header {
      padding: 1rem;
      flex-direction: column;
      text-align: center;

      .section-title {
        font-size: 1rem;
      }
    }

    .image-card .image-gallery-wrapper {
      height: 140px;
    }

    .upload-wrapper {
      padding: 1.5rem;
      flex-direction: column;
      text-align: center;

      .upload-label {
        flex-direction: column;

        .label-text {
          margin-left: 0;
          margin-top: 0.5rem;
          text-align: center;
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .professional-form-header .header-content {
      flex-direction: column;
      gap: 1rem;
    }
  }
  @media (max-width: 768px) {
    .main-form-container {
      margin: 0.5rem;
      border-radius: 10px !important;
    }
    .form-main-content {
      padding: 1rem;
    }
    .action-buttons {
      flex-direction: column;
    }
  }
  .natcon-alert-card {
    border-left: 4px solid #ff0505;
    background: linear-gradient(
      90deg,
      rgba(25, 118, 210, 0.03),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .natcon-alert-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #ff0505;
  }
  .natcon-alert-msg {
    color: #ff0505;
    margin-top: 6px;
    font-size: 0.95rem;
    line-height: 1.35;
  }
  .natcon-alert-extra {
    color: rgba(198, 40, 40, 0.95);
    font-size: 0.92rem;
    line-height: 1.3;
  }

  .natcon-success-card {
    border-left: 4px solid #2e7d32;
    background: linear-gradient(
      90deg,
      rgba(46, 125, 50, 0.04),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
}
</style>